import { Marker, Popup } from "react-map-gl";
import { Circle } from "@mui/icons-material";
import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { MarkerData, MarkerType } from "./GeoMarkerUtils";
import { useAppDispatch } from "../../app/hooks";
import { setIsShowingMarkerPopup } from "../../features/mapDisplay/mapDisplaySlice";

interface MapMarkersProps<T>
{
    items: Array<T>;
    iconScale: number;
    getMarkerData: ( item: T, index: number ) => MarkerData;
}

/**
 * Return true if the user planned this marker, false if the router inserted it
 * @param type the type of marker
 */
export function isPlannedWaypointType( type: MarkerType )
{
    switch ( type )
    {
        case MarkerType.BORDER_CROSSING:
        case MarkerType.WEATHER_DELAY:
        case MarkerType.OVERNIGHT_STAY_RECOMMENDED:
            return false;
        default:
            return true;
    }
}
export function wrapWaypointIcon( isPlannedWaypoint: boolean, iconScale: number )
{
    return (
        <Circle
            style = {{
                color: "black",
                position: "absolute",
                scale: `${iconScale}%`,
            }}
        />
    );
}

/**
 * Generate markers for the map.
 * @param {Array} items - Array of items with location information.
 * @param {number} iconScale - Scale of the marker icon.
 * @param {function} getMarkerData - Function to get marker data from each item.
 */
const MapMarkers = <T,>( { items, iconScale, getMarkerData }: MapMarkersProps<T> ) =>
{
    const [ activePopup, setActivePopup ] = useState<{ latitude: number, longitude: number, text: string, description: string, durationString?: string, useAlternativeColor: boolean } | null>( null );
    const dispatch = useAppDispatch();
    return (
        <React.Fragment>
            {items.map( ( item, index ) =>
            {
                const markerData = getMarkerData( item, index );

                if ( markerData.location !== undefined )
                {
                    const { latitude, longitude } = markerData.location;

                    const handleMouseEnter = () =>
                    {
                        setActivePopup( {
                            latitude,
                            longitude,
                            text: markerData.stopType,
                            description: markerData.description,
                            durationString: markerData.durationString,
                            useAlternativeColor: !markerData.isPlanned
                        } );
                        dispatch( setIsShowingMarkerPopup( true ) );
                    };

                    const handleMouseLeave = () =>
                    {
                        setActivePopup( null );
                        dispatch( setIsShowingMarkerPopup( false ) );
                    };

                    return (
                        <div key = {markerData.key}>
                            <Marker
                                latitude = {latitude}
                                longitude = {longitude}
                                anchor = "center"
                                offset = {[ -10, -10 ]} // Correct the icon offset
                                style = {{ zIndex: 0 }} // Ensure the icon is behind the RV icon
                            >
                                <div
                                    onMouseEnter = {handleMouseEnter}
                                    onMouseLeave = {handleMouseLeave}
                                >
                                    { wrapWaypointIcon( markerData.isPlanned, iconScale )}
                                    {markerData.icon}
                                </div>
                            </Marker>

                            { activePopup && (
                                <Popup
                                    latitude = {activePopup.latitude}
                                    longitude = {activePopup.longitude}
                                    closeButton = {false}
                                    closeOnClick = {false}
                                    style = {{ paddingBottom: "6px", zIndex: "2" }}
                                    className = {activePopup.useAlternativeColor ? "custom-Thumbnail-Marker-Popup-alternative" : "custom-Thumbnail-Marker-Popup"}
                                >
                                    <Box sx = {{ textAlign: "center", padding: "4px" }}>
                                        <Typography sx = {{ color: "white", fontWeight: "bold", fontSize: "14px" }}>
                                            {activePopup.text}
                                        </Typography>
                                        { activePopup.durationString !== undefined &&
                                            <Typography sx = {{ color: "white", fontWeight: "bold", fontSize: "14px" }}>
                                                {activePopup.durationString}
                                            </Typography>}
                                        <Typography sx = {{ color: "white", fontWeight: "normal", fontSize: "12px" }}>
                                            {activePopup.description}
                                        </Typography>
                                    </Box>
                                </Popup>
                            )}

                        </div>
                    );
                }
                return null;
            } )}
        </React.Fragment>
    );
};

export default MapMarkers;
